import { memo, useContext } from 'react';
import styled from '@emotion/styled';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming/Context.js';
import { pulse } from '@trinity/chat-components/utils';
import { shouldForwardProp } from '@trinity/chat-components/utils/emotion';
import { Lightbulb } from './Lightbulb';

type Props = {
  onClick?: () => void;
  themeVersion?: 'v1' | 'v2';
};

export const ChatWidget = memo(({ onClick, themeVersion }: Props) => {
  const theming = useContext(AppThemingContext);
  const bubbleColor = theming?.enabled ? theming.theme.color.bubble || undefined : undefined;

  return (
    <AppWidthWrapper>
      <Root
        bubbleColor={bubbleColor}
        themeVersion={themeVersion}
        type="button"
        aria-label="Reopen chat panel"
        onClick={onClick}
      >
        <Lightbulb size={28} color={bubbleColor} />
      </Root>
    </AppWidthWrapper>
  );
});

const WidgetSize = 75;
const positioning = 35;

type RootProps = {
  bubbleColor?: string;
  themeVersion?: 'v1' | 'v2';
};

const Root = styled('button', {
  shouldForwardProp: shouldForwardProp('bubbleColor'),
})<RootProps>(({ theme, themeVersion, bubbleColor }) => {
  const color = bubbleColor ?? theme.palette.primary.light6;

  return {
    position: 'absolute',

    bottom: 65,
    right: 0,
    '@media (min-width: 1465px)': {
      bottom: positioning,
      right: -WidgetSize,
    },

    width: WidgetSize,
    height: WidgetSize,
    borderRadius: '50%',
    border: 'none',
    padding: 0,

    background: bubbleColor ?? (themeVersion === 'v2' ? 'var(--pri-02)' : theme.palette.primary.light6),
    boxShadow: `0 0 0 0 ${color}`,
    cursor: 'pointer',

    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    animation: `${pulse(color)} 6s infinite`,
  };
});

const AppWidthWrapper = styled('div')(({ theme }) => ({
  position: 'fixed',
  maxWidth: theme.breakpoints.values.navbarWidth,
  margin: '0 auto',
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: theme.zIndex.widget,
  boxSizing: 'border-box',
}));
